import type {
  FloraCategory,
  FloraDefinition,
  FloraGeometryForm,
} from '@/flora/FloraDefinition';
import type { FloraInstance, PruningStyle } from '@/flora/FloraInstance';
import { computeGrowthState, type GrowthState } from '@/flora/FloraGrowthModel';
import { foliagePresence, resolveSeasonalAppearance } from '@/flora/FloraSeasonModel';
import type { FloraResolver } from '@/localization/FloraResolver';
import type { EnvironmentState } from '@/environment/EnvironmentState';
import { computeSunPosition } from '@/environment/SunModel';
import { dateFromDayOfYear, isFreezingSeason, seasonBlend } from '@/environment/SeasonModel';
import { describeGroundCover } from '@/terrain/WinterGround';
import {
  totalHeight,
  type PavingMaterial,
  type RoofShape,
  type SiteObject,
  type SiteObjectKind,
} from '@/site/SiteObject';
import type { PropDefinition } from '@/site/PropCatalog';
import {
  defaultPose,
  poseHeightMeters,
  poseLengthMeters,
  type FaunaAttachment,
  type FaunaClass,
  type FaunaDefinition,
} from '@/fauna/FaunaDefinition';
import { instanceScale, type FaunaInstance } from '@/fauna/FaunaInstance';
import { surfaceLayers, type SurfaceLayerId } from '@/terrain/TerrainSurface';
import { EMPHASIS_WEIGHT, type SemanticTag } from '@/semantics/SemanticTag';
import {
  descriptorFromTag,
  faunaSemantics,
  floraSemantics,
  groundSemantics,
  structureSemantics,
  type SemanticDescriptor,
} from '@/semantics/SemanticProfile';
import { assignSpeciesColors, faunaKeyId, siteObjectKeyId } from './SpeciesPalette';
import { describeColor } from './ColorNames';
import type { ImageSize } from './ImageSpec';

/**
 * The scene manifest: everything the AI enhancement pass needs to know about a captured frame that
 * the pixels themselves cannot say.
 *
 * A procedurally rendered maple reads as *a tree*. Nothing in the image says `Acer rubrum`, 5.4 m
 * tall, 60% through leaf drop, scarlet. We already hold every one of those facts in canonical domain
 * data — this module gathers them, works out **where in the frame** each plant landed, and emits a
 * structured record. `PromptBuilder` turns that record into prose; the ID-map render pass paints the
 * same species key colours into a second image so the model can bind name to silhouette.
 *
 * Deliberately free of Three.js: projection arrives as a callback so the whole thing is unit-tested
 * against a fake camera, the same split `ui/FloraFacets.ts` uses for browse logic.
 */

/** A world-space point, matching `FloraInstance.position`. */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Result of projecting a world point through the capture camera. */
export interface ProjectedPoint {
  /** Normalised device X in [-1, 1], left to right. */
  x: number;
  /** Normalised device Y in [-1, 1], bottom to top. */
  y: number;
  /** Straight-line distance from the camera in meters. */
  distanceMeters: number;
  /** True when the point falls behind the camera plane (its x/y are meaningless). */
  behindCamera: boolean;
}

export type ProjectFn = (world: Vec3) => ProjectedPoint;

/** Where a subject sits in frame: horizontal third × depth band. */
export type FrameZone =
  | 'left foreground'
  | 'centre foreground'
  | 'right foreground'
  | 'left midground'
  | 'centre midground'
  | 'right midground'
  | 'left background'
  | 'centre background'
  | 'right background';

export interface ManifestScreen {
  /** 0 at the left edge of frame, 1 at the right. */
  x: number;
  /** 0 at the top of frame, 1 at the bottom (base of the plant). */
  y: number;
  distanceMeters: number;
  /** Projected height as a fraction of frame height — how big the plant reads. */
  heightFraction: number;
  zone: FrameZone;
}

export interface ManifestAppearance {
  foliageColor: string;
  /** Human colour word for the prompt — "deep scarlet" beats "#c1350f". */
  foliageColorName: string;
  foliageDensity: number;
  branchVisibility: number;
  dormant: boolean;
  /** 0 = fully leafed, 1 = bare. Derived from the season model's leaf-out/drop ramps. */
  leafDropFraction: number;
  flowerColor: string | null;
  flowerColorName: string | null;
  flowerDensity: number;
  fruitColor: string | null;
  fruitColorName: string | null;
  fruitDensity: number;
  /** One-line prose summary, e.g. "deep scarlet foliage, 60% dropped, bare branches showing". */
  phrase: string;
}

/**
 * One named entry in the manifest. Usually a single plant; when several of the same species share a
 * zone they collapse into one subject with `count > 1` (a drift), which keeps the prompt readable
 * and matches how a planting plan is actually described.
 */
export interface ManifestSubject {
  definitionId: string;
  /** ID-map key colour — the binding between this description and the second input image. */
  keyColor: string;
  botanicalName: string;
  commonName: string;
  cultivar?: string;
  category: FloraCategory;
  geometryForm: FloraGeometryForm;
  count: number;
  instanceIds: string[];
  /** Representative dimensions (the most prominent member of a drift). */
  heightMeters: number;
  spreadMeters: number;
  canopyBaseMeters: number;
  pruningStyle: PruningStyle;
  appearance: ManifestAppearance;
  screen: ManifestScreen;
  /**
   * What this planting *means* — the species' catalog tags, this specimen's condition, and anything
   * the designer tagged on it or on the ground it stands in.
   *
   * Carried per subject rather than pooled at the end of the manifest because a tag is only useful
   * to an image model when it is bound to a silhouette, and the subject line is where that binding
   * already lives.
   */
  semantics: SemanticDescriptor[];
}

/**
 * A building or paved area in frame.
 *
 * This is the channel that stops an enhancement pass from inventing architecture. The beauty render
 * shows a plain coloured box; without a manifest entry the image model is free to decide it is a
 * garage, a pool house, or nothing at all, and it will move it. Told "a 5.5 × 3.5 m gambrel-roofed
 * outbuilding, deep red board walls, pale grey roof, right midground" — with a key colour binding
 * that description to an exact silhouette in the ID map — it renders the shed that is actually
 * there. Crude geometry, correct picture.
 */
export interface ManifestStructure {
  id: string;
  kind: SiteObjectKind;
  name: string;
  /** ID-map key colour — the binding between this description and the second input image. */
  keyColor: string;
  widthMeters: number;
  depthMeters: number;
  /** Total height including the roof; 0 for paving. */
  heightMeters: number;
  roof: RoofShape | null;
  paving: PavingMaterial | null;
  wallColor: string;
  wallColorName: string;
  roofColor: string | null;
  roofColorName: string | null;
  screen: ManifestScreen;
  /** One-line prose summary for the prompt. */
  phrase: string;
  /**
   * What this built element means: its material character, a prop's catalog identity, and any tag the
   * designer put on it or on the ground under it.
   *
   * This is the channel that turns "a 1.6 × 0.6 m box" into "a slatted teak bench". Props in
   * particular have no honest procedural description — their shape *is* their identity — so without
   * this a placed bench reached the prompt as a nameless volume and came back as whatever the model
   * felt like putting there.
   */
  semantics: SemanticDescriptor[];
}

/**
 * A painted ground material present on the site, and what it is.
 *
 * Ground is the largest surface in nearly every frame and, until tags existed, the one the prompt
 * said the least about: the beauty pass shows a flat colour, the ID map paints ground black by
 * design, and the manifest never named a material at all. So a deliberately painted gravel courtyard
 * arrived at the model as an ambiguous grey field, and came back as lawn about as often as not.
 */
export interface ManifestGround {
  layer: SurfaceLayerId;
  name: string;
  /**
   * Share of the *visible ground in this frame* under this material, 0–1.
   *
   * Through the frame rather than across the site, for the same reason plants outside the frustum
   * are dropped: the prompt describes a picture. A site-wide share reported a stream crossing the
   * middle of the view as 2 % and a courtyard filling it as 8 %, which reads as trim either way.
   */
  coverage: number;
  semantics: SemanticDescriptor[];
}

/**
 * A tagged place, projected into the frame.
 *
 * Location tags are the only part of the semantic layer with no geometry of their own, so they are
 * bound to the picture the one way that is left: by where their centre lands and how much of the
 * frame their circle spans. That is enough for "the right foreground is the fire-pit lounge", which
 * is the sentence the tag exists to produce.
 */
export interface ManifestPlaceTag {
  /**
   * The tag this came from. Carried so a reference photograph attached to the same tag can be bound
   * to this place in the prompt — see `RenderReferences.ts`. Nothing else reads it.
   */
  tagId: string;
  label: string;
  note: string | null;
  category: SemanticTag['category'];
  emphasis: SemanticTag['emphasis'];
  radiusMeters: number;
  screen: ManifestScreen;
  /** Botanical names of the planting standing inside this place, so the prompt can bind the two. */
  subjectNames: string[];
}

/**
 * A creature, figure, or group of them in frame.
 *
 * The third and last thing that can be put on a site, and the one the enhancement pass handled worst
 * — by not handling it at all. A placed deer reached the model as an unexplained low-polygon mass in
 * the beauty render, painted black in the ID map (which reads as "ground, nothing here"), described
 * nowhere in the prompt, and standing inside a structure lock that says *add nothing that is not
 * listed below*. Every one of those says "this is not real", so the render came back with the deer
 * smoothed away, or left as the grey blob it was drawn as.
 *
 * This is the channel that fixes it, and it is the same channel buildings already had: a key colour
 * binding a description to an exact silhouette, plus enough of the record to render from — the
 * species, the pose, the measured size, and whether the thing is standing, perched or in flight.
 *
 * Grouped like plants rather than listed like buildings. Fourteen goldfinches in one corner of frame
 * is one fact, and `PromptBuilder` reads better saying so once.
 */
export interface ManifestCreature {
  definitionId: string;
  /** ID-map key colour — the binding between this description and the second input image. */
  keyColor: string;
  /** Display name: "Eastern cottontail", "Adult figure". */
  name: string;
  /** Absent for `person` records, which have no species. */
  scientificName?: string;
  faunaClass: FaunaClass;
  /** How it meets the world — the fact a flat silhouette states worst. */
  attachment: FaunaAttachment;
  /** The pose's display name, e.g. "Alert". */
  poseName: string;
  count: number;
  instanceIds: string[];
  /** Measured in the pose it is actually in, scaled by this instance's own variation. */
  heightMeters: number;
  lengthMeters: number;
  screen: ManifestScreen;
  /** One-line prose summary for the prompt. */
  phrase: string;
  semantics: SemanticDescriptor[];
}

export interface ManifestSpeciesTally {
  definitionId: string;
  keyColor: string;
  botanicalName: string;
  commonName: string;
  count: number;
}

/**
 * A creature in the ID-map legend.
 *
 * Separate from {@link ManifestSpeciesTally} rather than reusing it: that type's two name fields are
 * `botanicalName` and `commonName`, and a figure has neither. Bending "Adult figure" into a
 * botanical name to save a type would put it in the prompt as a plant.
 */
export interface ManifestCreatureTally {
  definitionId: string;
  keyColor: string;
  name: string;
  scientificName?: string;
  faunaClass: FaunaClass;
  count: number;
}

export interface ManifestEnvironment {
  timeLabel: string;
  hour: number;
  minute: number;
  dayOfYear: number;
  dateLabel: string;
  seasonLabel: string;
  weather: string;
  /**
   * The snow and/or ice lying on the ground, or null when it is bare.
   *
   * Separate from `weather`, which describes the air. Without this a February capture goes to the
   * image model as a date and a low sun and comes back as a green lawn, because nothing in the
   * manifest ever said the site was under snow.
   */
  groundCover: string | null;
  latitudeDegrees: number;
  sunAzimuthDegrees: number;
  sunElevationDegrees: number;
  /** Prose for the light, e.g. "low golden light from the west, 8° above the horizon". */
  lightPhrase: string;
}

export interface ManifestCamera {
  fieldOfViewDegrees: number;
  /** Camera height above the ground plane, meters. */
  heightMeters: number;
  /** Negative = looking down. */
  pitchDegrees: number;
  distanceMeters: number;
  /** Prose for the vantage, e.g. "eye-level view from 18 m, looking slightly down". */
  phrase: string;
}

export interface ManifestSite {
  templateName: string;
  widthMeters: number;
  lengthMeters: number;
  /**
   * Which document this frame was taken of, when there was one.
   *
   * Nothing in the prompt reads it — an image model does not care what the file is called. It is
   * here because a shot outlives the session that took it, and the gallery has to be able to answer
   * *can this frame be put back*: re-staging restores a camera and a date onto whatever site is
   * currently open, which is only meaningful if it is the same site. Absent means the site had never
   * been saved when the shutter fired, which is different from belonging to nothing — so the
   * gallery says so rather than offering a re-stage it cannot honour.
   *
   * Carried on the manifest rather than beside it because the manifest is the one part of a shot
   * both stores keep verbatim: the local store writes the object straight into IndexedDB and the
   * account store round-trips it as opaque JSON. A new top-level field on the record would have
   * needed the server to learn about it first.
   */
  id?: string;
  /** The document's name at capture time, for a gallery caption. Renames do not chase it. */
  name?: string;
}

export interface SceneManifest {
  version: number;
  capturedAt: string;
  image: ImageSize;
  camera: ManifestCamera;
  environment: ManifestEnvironment;
  site: ManifestSite | null;
  /** Individually described subjects, most prominent first. */
  subjects: ManifestSubject[];
  /** Buildings and paving visible in frame, largest first. Always named individually. */
  structures: ManifestStructure[];
  /** Creatures and figures visible in frame, largest first. Always named individually. */
  creatures: ManifestCreature[];
  /** Species present in frame but not individually named, summarised. */
  additional: ManifestSpeciesTally[];
  /** Every species visible in frame, for the ID-map legend. */
  legend: ManifestSpeciesTally[];
  /** Every creature species visible in frame, for the ID-map legend. */
  creatureLegend: ManifestCreatureTally[];
  /** Painted ground materials present on the site, largest share first. */
  ground: ManifestGround[];
  /** Tagged places whose centre falls in frame, loudest first. */
  places: ManifestPlaceTag[];
  /** Whole-site tags — the register the entire render should sit inside. */
  siteSemantics: SemanticDescriptor[];
  /** Total plants visible in frame (including collapsed and unnamed ones). */
  visiblePlantCount: number;
  /** Total site objects visible in frame. */
  visibleStructureCount: number;
  /** Total creatures and figures visible in frame (including grouped ones). */
  visibleCreatureCount: number;
}

export interface ManifestPlantInput {
  instance: FloraInstance;
  definition: FloraDefinition;
}

/**
 * A site object plus the absolute ground height it sits at. The manifest stays free of terrain (and
 * of Three.js) the same way it stays free of a camera — the caller resolves the world, this module
 * only describes it.
 */
export interface ManifestStructureInput {
  object: SiteObject;
  /** Absolute world y of the footprint base. */
  baseY: number;
}

/**
 * A placed creature plus the absolute ground height under it, and the record behind it.
 *
 * The height is resolved by the caller for the same reason a structure's is: this module stays free
 * of terrain and of Three.js. A creature's own `position.y` is only an offset above the ground —
 * projecting it directly would put every animal on a hillside at sea level.
 */
export interface ManifestCreatureInput {
  instance: FaunaInstance;
  definition: FaunaDefinition;
  /** Absolute world y the creature's feet (or hover point) sit at. */
  baseY: number;
}

export interface SceneManifestInput {
  plants: readonly ManifestPlantInput[];
  structures?: readonly ManifestStructureInput[];
  /** Placed creatures and figures. Absent in tests and in any build with no fauna world. */
  creatures?: readonly ManifestCreatureInput[];
  environment: EnvironmentState;
  /** The day actually driving appearance — `EnvironmentManager.effectiveDayOfYear()`. */
  effectiveDayOfYear: number;
  camera: Omit<ManifestCamera, 'phrase'>;
  image: ImageSize;
  site?: ManifestSite | null;
  project: ProjectFn;
  /** Fixed key colours; derived from the visible species when omitted. */
  speciesColors?: ReadonlyMap<string, string>;
  /** How many subjects get an individual description before the tail is summarised. */
  maxNamedSubjects?: number;
  /**
   * Manual semantic tags on the site — every scope, not only the ones in frame. The manifest decides
   * which are relevant to *this* frame; the caller just hands over the document's list.
   */
  tags?: readonly SemanticTag[];
  /**
   * Resolve a site object's authored asset, so a prop is described by its catalog identity rather
   * than by the placeholder box the renderer draws for it. Absent in tests and wherever the prop
   * library is not loaded, which degrades to "a small garden fixture" rather than to a lie.
   */
  prop?: (object: SiteObject) => PropDefinition | undefined;
  /**
   * Fraction of the *visible ground* under each material, measured through this frame by
   * `CaptureService.groundCoverageInView`. A site-wide share (`Terrain.coverage()`) is still
   * accepted and still describes the ground correctly — only less usefully.
   */
  groundCoverage?: Readonly<Record<SurfaceLayerId, number>> | null;
  /** Overrides `new Date()` so tests and snapshots stay stable. */
  now?: Date;
  /**
   * The site's flora resolver.
   *
   * Given one, every plant is described against this site's climate — the same resolved calendar the
   * renderer drew the frame from. That matters more here than almost anywhere: this manifest is what
   * an image model is told the picture contains, and a prompt describing bare branches over a frame
   * showing full leaf is the one failure that produces a confidently wrong render. Absent, plants are
   * described against the catalog's reference climate, exactly as before.
   */
  resolver?: FloraResolver;
}

/**
 * Bumped to 2 when the manifest gained semantics: per-subject tags, ground materials, and places.
 * Bumped to 3 when it gained creatures — the third placeable class, previously described nowhere.
 */
export const SCENE_MANIFEST_VERSION = 3;

/** Default cap on individually named subjects — beyond this a prompt stops being read carefully. */
const DEFAULT_MAX_NAMED = 14;

/** Plants projecting to less than this fraction of frame height are noise, not subjects. */
const MIN_HEIGHT_FRACTION = 0.012;

/** How far outside the frame a plant may sit and still count as visible (partial entries). */
const NDC_MARGIN = 1.15;

/**
 * Ground materials below this share of the visible ground are not described.
 *
 * A stray dab of gravel is not a design decision, and listing it would spend a prompt line implying
 * one. Grass is exempt: it is the base layer, so a site with no paint at all is entirely lawn and
 * saying so is the single most useful thing the ground section can do.
 *
 * Low, and deliberately. The old floor was 2 % *of the site*, which quietly deleted exactly the
 * features that most need naming — a stream, a pond, a path — because a linear feature covers very
 * little ground however much of the picture it occupies. Now that the share is measured through the
 * frame, anything reaching half a percent of the visible ground is genuinely in shot, and the cost
 * of one extra line is far below the cost of the model paving a watercourse with lawn.
 */
const MIN_GROUND_COVERAGE = 0.005;

export function buildSceneManifest(input: SceneManifestInput): SceneManifest {
  const maxNamed = input.maxNamedSubjects ?? DEFAULT_MAX_NAMED;
  const now = input.now ?? new Date();
  const tags = indexTags(input.tags ?? []);

  const visible = collectVisible(input);

  // One palette call covers plants and structures together: their ids share a namespace here, so a
  // building can never be handed the same key colour as a species.
  const speciesColors =
    input.speciesColors ??
    assignSpeciesColors([
      ...visible.map((v) => v.definition.id),
      ...(input.structures ?? []).map((s) => siteObjectKeyId(s.object.id)),
      ...(input.creatures ?? []).map((c) => faunaKeyId(c.definition.id)),
    ]);

  // Plants and buildings are banded against one depth range. Judging them separately would call a
  // shed "midground" while the tree beside it reads "foreground" — the two must agree.
  const structureDistances = (input.structures ?? []).map((s) =>
    input.project({ x: s.object.position.x, y: s.baseY, z: s.object.position.z }).distanceMeters,
  );
  // Creatures band with everything else, for the reason buildings do: a deer described as
  // "background" while the shrub it is standing behind reads "midground" is a contradiction the
  // model has to resolve, and it resolves it by moving something.
  const creatureDistances = (input.creatures ?? []).map((c) =>
    input.project({ x: c.instance.position.x, y: c.baseY, z: c.instance.position.z }).distanceMeters,
  );
  const bandFor = depthBander([
    ...visible.map((v) => v.distanceMeters),
    ...structureDistances,
    ...creatureDistances,
  ]);

  const banded = applyDepthBands(visible, bandFor);
  const subjects = collapseDrifts(banded, speciesColors, tags);
  subjects.sort((a, b) => prominence(b) - prominence(a));

  const structures = collectStructures(input, speciesColors, bandFor, tags);
  const creatures = collectCreatures(input, speciesColors, bandFor, tags);

  const named = subjects.slice(0, maxNamed);
  const tail = subjects.slice(maxNamed);

  return {
    version: SCENE_MANIFEST_VERSION,
    capturedAt: now.toISOString(),
    image: { width: input.image.width, height: input.image.height },
    camera: { ...input.camera, phrase: describeCamera(input.camera) },
    environment: describeEnvironment(input.environment, input.effectiveDayOfYear),
    site: input.site ?? null,
    subjects: named,
    structures,
    creatures,
    additional: tally(tail),
    legend: tally(subjects),
    creatureLegend: tallyCreatures(creatures),
    ground: collectGround(input, tags),
    places: collectPlaces(input, tags, banded, bandFor),
    siteSemantics: tags.site().map(descriptorFromTag),
    visiblePlantCount: visible.length,
    visibleStructureCount: structures.length,
    visibleCreatureCount: creatures.reduce((total, entry) => total + entry.count, 0),
  };
}

// ---------------------------------------------------------------------------------------------
// Semantics
// ---------------------------------------------------------------------------------------------

/**
 * Tags grouped by what they are about.
 *
 * Built once per capture and threaded through, rather than re-filtering the array at every subject:
 * a crowded site is a few hundred plants against a few dozen tags, and the naive form is the product
 * of the two for no reason. `at` stays a linear scan over location tags only, which is the query
 * that genuinely needs geometry rather than a key.
 */
interface TagIndex {
  plant(id: string): SemanticTag[];
  structure(id: string): SemanticTag[];
  ground(layer: SurfaceLayerId): SemanticTag[];
  site(): SemanticTag[];
  locations(): SemanticTag[];
  /** Location tags whose circle contains a point. */
  at(x: number, z: number): SemanticTag[];
}

function indexTags(tags: readonly SemanticTag[]): TagIndex {
  const byKey = new Map<string, SemanticTag[]>();
  const locations: SemanticTag[] = [];

  for (const tag of tags) {
    if (tag.target.kind === 'location') {
      locations.push(tag);
      continue;
    }
    const key =
      tag.target.kind === 'plant'
        ? `plant:${tag.target.instanceId}`
        : tag.target.kind === 'structure'
          ? `structure:${tag.target.objectId}`
          : tag.target.kind === 'ground'
            ? `ground:${tag.target.layer}`
            : 'site';
    const bucket = byKey.get(key);
    if (bucket) bucket.push(tag);
    else byKey.set(key, [tag]);
  }

  const get = (key: string): SemanticTag[] => byKey.get(key) ?? [];
  return {
    plant: (id) => get(`plant:${id}`),
    structure: (id) => get(`structure:${id}`),
    ground: (layer) => get(`ground:${layer}`),
    site: () => get('site'),
    locations: () => locations,
    at: (x, z) =>
      locations.filter(
        (tag) =>
          tag.target.kind === 'location' &&
          Math.hypot(tag.target.x - x, tag.target.z - z) <= tag.target.radiusMeters,
      ),
  };
}

/**
 * Painted ground, described.
 *
 * Ordered by share of the site rather than by the layer list, so the material a render should get
 * right first is the one stated first. Grass survives the coverage floor because the absence of paint
 * *is* a statement — an unpainted site is entirely lawn, and that is worth one line.
 */
function collectGround(input: SceneManifestInput, tags: TagIndex): ManifestGround[] {
  const coverage = input.groundCoverage;
  if (!coverage) return [];

  const result: ManifestGround[] = [];
  for (const layer of surfaceLayers()) {
    const share = coverage[layer.id] ?? 0;
    if (share < MIN_GROUND_COVERAGE && layer.id !== 'grass') continue;
    if (share <= 0) continue;
    result.push({
      layer: layer.id,
      name: layer.name,
      coverage: round3(share),
      semantics: groundSemantics(layer.id, tags.ground(layer.id)).descriptors,
    });
  }
  return result.sort((a, b) => b.coverage - a.coverage);
}

/**
 * Tagged places, projected into the frame.
 *
 * A place whose centre falls outside the frame is dropped, the same rule plants follow and for the
 * same reason: describing ground the model cannot see invites it to make room for it. The planting
 * standing inside each place is listed so the prompt can say *which* shapes in the picture the label
 * applies to — a name with nothing bound to it is decoration.
 */
function collectPlaces(
  input: SceneManifestInput,
  tags: TagIndex,
  plants: readonly BandedPlant[],
  bandFor: (distance: number) => number,
): ManifestPlaceTag[] {
  const places: ManifestPlaceTag[] = [];

  for (const tag of tags.locations()) {
    if (tag.target.kind !== 'location') continue;
    const { x, z, radiusMeters } = tag.target;
    // Ground height is unknown here — the manifest holds no terrain — so the tag is projected at the
    // height of the plants standing in it, falling back to the site datum. Good enough for a zone.
    const inside = plants.filter(
      (plant) => Math.hypot(plant.instance.position.x - x, plant.instance.position.z - z) <= radiusMeters,
    );
    const y = inside[0]?.instance.position.y ?? 0;

    const centre = input.project({ x, y, z });
    if (centre.behindCamera) continue;
    if (Math.abs(centre.x) > NDC_MARGIN || Math.abs(centre.y) > NDC_MARGIN) continue;

    // Frame span of the circle, measured across it rather than assumed from distance — an oblique
    // view foreshortens a place, and a prompt that oversells its size invites the model to clear one.
    const edge = input.project({ x: x + radiusMeters, y, z });
    const heightFraction = Math.abs(edge.x - centre.x) / 2;

    places.push({
      tagId: tag.id,
      label: tag.label,
      note: tag.note ?? null,
      category: tag.category,
      emphasis: tag.emphasis,
      radiusMeters: round2(radiusMeters),
      screen: {
        x: round3((centre.x + 1) / 2),
        y: round3(1 - (centre.y + 1) / 2),
        distanceMeters: round2(centre.distanceMeters),
        heightFraction: round3(heightFraction),
        zone: describeZone(centre.x, bandFor(centre.distanceMeters)),
      },
      subjectNames: [...new Set(inside.map((plant) => plant.definition.botanicalName))].sort(),
    });
  }

  return places.sort(
    (a, b) => EMPHASIS_WEIGHT[b.emphasis] - EMPHASIS_WEIGHT[a.emphasis] || b.screen.heightFraction - a.screen.heightFraction,
  );
}

// ---------------------------------------------------------------------------------------------
// Structures
// ---------------------------------------------------------------------------------------------

/**
 * Project every site object and describe the ones the camera can see. Unlike plants these are never
 * collapsed or truncated: a frame holds a handful of buildings at most, and each one is exactly the
 * kind of large, unmistakable object whose omission from the prompt lets the model improvise.
 */
function collectStructures(
  input: SceneManifestInput,
  keyColors: ReadonlyMap<string, string>,
  bandFor: (distance: number) => number,
  tags: TagIndex,
): ManifestStructure[] {
  const result: ManifestStructure[] = [];

  for (const { object, baseY } of input.structures ?? []) {
    if (object.hidden) continue;

    // `totalHeight` solves roofs and masses, which only buildings have. A prop's height is measured
    // off its asset and stored on the record, and reading 0 for one is what made every placed bench
    // project as a zero-height sliver and get culled before it could reach the prompt.
    const height =
      object.kind === 'building'
        ? totalHeight(object)
        : object.kind === 'prop'
          ? object.heightMeters
          : 0;
    const base = input.project({ x: object.position.x, y: baseY, z: object.position.z });
    const top = input.project({
      x: object.position.x,
      y: baseY + Math.max(height, 0.05),
      z: object.position.z,
    });
    if (base.behindCamera || top.behindCamera) continue;
    if (Math.abs(base.x) > NDC_MARGIN || Math.abs(base.y) > NDC_MARGIN) continue;

    // Paving is flat, so its projected height says nothing about how much frame it occupies. Fall
    // back to its footprint's on-screen scale instead of culling every patio as "too small".
    const heightFraction =
      object.kind === 'paving'
        ? Math.abs(
            input.project({
              x: object.position.x,
              y: baseY,
              z: object.position.z + object.depthMeters / 2,
            }).y - base.y,
          ) / 2
        : Math.abs(top.y - base.y) / 2;
    if (heightFraction < MIN_HEIGHT_FRACTION) continue;

    const isBuilding = object.kind === 'building';
    const structure: ManifestStructure = {
      id: object.id,
      kind: object.kind,
      name: object.name,
      keyColor: keyColors.get(siteObjectKeyId(object.id)) ?? '#ffffff',
      widthMeters: round2(object.widthMeters),
      depthMeters: round2(object.depthMeters),
      heightMeters: round2(height),
      roof: isBuilding ? object.roof : null,
      // Only paving has a paving material. A prop's `paving` field is an unused default from the
      // shared record, and reporting it would tell the prompt a bench is made of concrete.
      paving: object.kind === 'paving' ? object.paving : null,
      wallColor: object.wallColor,
      wallColorName: describeColor(object.wallColor),
      roofColor: isBuilding ? object.roofColor : null,
      roofColorName: isBuilding ? describeColor(object.roofColor) : null,
      screen: {
        x: round3((base.x + 1) / 2),
        y: round3(1 - (base.y + 1) / 2),
        distanceMeters: round2(base.distanceMeters),
        heightFraction: round3(heightFraction),
        zone: describeZone(base.x, bandFor(base.distanceMeters)),
      },
      phrase: '',
      semantics: structureSemantics(object, input.prop?.(object), [
        ...tags.structure(object.id),
        // A place tag covering this object describes it too: "the fire-pit lounge" is a fact about
        // the bench standing in it, and binding it here is what lets the prompt say so on the line
        // that already names the bench.
        ...tags.at(object.position.x, object.position.z),
      ]).descriptors,
    };
    structure.phrase = structurePhrase(structure);
    result.push(structure);
  }

  result.sort((a, b) => b.screen.heightFraction - a.screen.heightFraction);
  return result;
}

const ROOF_WORDS: Record<RoofShape, string> = {
  flat: 'flat-roofed',
  shed: 'mono-pitch',
  gable: 'gable-roofed',
  gambrel: 'gambrel-roofed',
  hip: 'hip-roofed',
};

function structurePhrase(structure: ManifestStructure): string {
  const size = `${structure.widthMeters} m × ${structure.depthMeters} m`;

  if (structure.kind === 'paving') {
    return `a ${size} area of ${structure.paving ?? 'paving'}, ${structure.wallColorName}`;
  }

  // A prop is the one kind whose shape carries no information: the renderer draws an authored mesh,
  // or a box standing in for one. So its phrase reports only the envelope it occupies, and the
  // semantics beside it say what actually sits there. Describing it as a "building" — which is what
  // this function used to do, since props inherit the building record — put a shed in the prompt
  // wherever the user had placed a bench.
  if (structure.kind === 'prop') {
    return `a ${size} footprint standing ${structure.heightMeters} m tall, ${structure.wallColorName}`;
  }

  const roof = structure.roof ? `${ROOF_WORDS[structure.roof]} ` : '';
  // Only a roof that actually has a ridge gets described as having one.
  const apex =
    structure.roof === 'gable' || structure.roof === 'gambrel' || structure.roof === 'hip'
      ? 'to the ridge'
      : 'at its highest';

  // When the walls and roof land on the same colour word, saying it twice reads as a mistake.
  const materials =
    structure.roofColorName !== null && structure.roofColorName === structure.wallColorName
      ? `${structure.wallColorName} throughout`
      : `${structure.wallColorName} walls with a ${structure.roofColorName ?? 'grey'} roof`;

  return `a ${size} ${roof}building ${structure.heightMeters} m ${apex}, ${materials}`;
}

// ---------------------------------------------------------------------------------------------
// Creatures
// ---------------------------------------------------------------------------------------------

/**
 * Project every placed creature and describe the ones the camera can see.
 *
 * Grouped by species **and pose and zone**, which is one key more than plants use, because the pose
 * is most of what a creature's line says. Two robins in the same corner of frame, one perched and
 * one in flight, are two different pictures and collapsing them would describe neither.
 *
 * Nothing is truncated. Even a large scene holds a handful of these, and a creature the prompt does
 * not name is precisely the one the model deletes.
 */
function collectCreatures(
  input: SceneManifestInput,
  keyColors: ReadonlyMap<string, string>,
  bandFor: (distance: number) => number,
  tags: TagIndex,
): ManifestCreature[] {
  const groups = new Map<string, ManifestCreature>();

  for (const { instance, definition, baseY } of input.creatures ?? []) {
    if (instance.hidden) continue;

    const pose = definition.poses.find((candidate) => candidate.id === instance.poseId)
      ?? defaultPose(definition);
    const scale = instanceScale(instance);
    const height = poseHeightMeters(definition, pose) * scale;
    const length = poseLengthMeters(definition, pose) * scale;

    const foot = { x: instance.position.x, y: baseY, z: instance.position.z };
    const base = input.project(foot);
    const top = input.project({ ...foot, y: baseY + Math.max(height, 0.02) });
    if (base.behindCamera || top.behindCamera) continue;
    if (Math.abs(base.x) > NDC_MARGIN || Math.abs(base.y) > NDC_MARGIN) continue;

    // No minimum size. A plant below the threshold is one more shrub in a bed and costs nothing to
    // omit; a 30 mm monarch is deliberate staffage, and the frame it occupies is not the measure of
    // whether the designer meant it to be there.
    const heightFraction = Math.abs(top.y - base.y) / 2;
    const zone = describeZone(base.x, bandFor(base.distanceMeters));
    const key = `${definition.id}|${pose.id}|${instance.attachment}|${zone}`;

    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      existing.instanceIds.push(instance.id);
      // The group is described by its most prominent member, so the size in the line matches the
      // silhouette the eye goes to first.
      if (heightFraction > existing.screen.heightFraction) {
        existing.heightMeters = round2(height);
        existing.lengthMeters = round2(length);
        existing.screen = {
          x: round3((base.x + 1) / 2),
          y: round3(1 - (base.y + 1) / 2),
          distanceMeters: round2(base.distanceMeters),
          heightFraction: round3(heightFraction),
          zone,
        };
      }
      continue;
    }

    groups.set(key, {
      definitionId: definition.id,
      keyColor: keyColors.get(faunaKeyId(definition.id)) ?? '#ffffff',
      name: definition.name,
      ...(definition.scientificName !== undefined
        ? { scientificName: definition.scientificName }
        : {}),
      faunaClass: definition.class,
      attachment: instance.attachment,
      poseName: pose.name,
      count: 1,
      instanceIds: [instance.id],
      heightMeters: round2(height),
      lengthMeters: round2(length),
      screen: {
        x: round3((base.x + 1) / 2),
        y: round3(1 - (base.y + 1) / 2),
        distanceMeters: round2(base.distanceMeters),
        heightFraction: round3(heightFraction),
        zone,
      },
      phrase: '',
      semantics: faunaSemantics(definition, instance, [
        // A place tag covering this spot describes what is standing in it: "the fire-pit lounge" is
        // a fact about the figure sitting there, and this is the line that can say so.
        ...tags.at(instance.position.x, instance.position.z),
      ]).descriptors,
    });
  }

  const result = [...groups.values()];
  for (const creature of result) creature.phrase = creaturePhrase(creature);
  result.sort((a, b) => b.screen.heightFraction - a.screen.heightFraction);
  return result;
}

/**
 * A creature's line.
 *
 * Leads with the measured size, because scale is the whole job staffage does: a figure that renders
 * 2.4 m tall has broken the picture it was placed to fix, and the size is the one number the model
 * can check its output against. The pose and the attachment follow, since those are what the flat
 * silhouette in image 1 says least clearly.
 */
function creaturePhrase(creature: ManifestCreature): string {
  const size =
    creature.faunaClass === 'person'
      ? `${creature.heightMeters} m tall`
      : `${creature.heightMeters} m tall and ${creature.lengthMeters} m long`;

  // The authored capitalisation is kept, unlike a structure's. Fauna display names routinely open
  // with a proper adjective — "American robin", "Eastern cottontail" — and lowercasing those puts a
  // spelling error in the prompt, which is a worse fault than the stray capital in "a single
  // White-tailed deer".
  const what =
    creature.count > 1
      ? `${creature.count} ${creature.name}${plural(creature)}`
      : `a single ${creature.name}`;

  return `${what}, ${size}, ${creature.poseName.toLowerCase()}`;
}

/**
 * Pluralise a display name well enough for a prompt. Deliberately crude: the alternative is a
 * plural field on every catalog record, which is a lot of authoring to avoid "goldfinchs".
 */
function plural(creature: ManifestCreature): string {
  const name = creature.name.toLowerCase();
  if (name.endsWith('s') || name.endsWith('x') || name.endsWith('sh') || name.endsWith('ch')) {
    return 'es';
  }
  if (name.endsWith('y') && !/[aeiou]y$/.test(name)) return '';
  return 's';
}

function tallyCreatures(creatures: readonly ManifestCreature[]): ManifestCreatureTally[] {
  const byDefinition = new Map<string, ManifestCreatureTally>();
  for (const creature of creatures) {
    const existing = byDefinition.get(creature.definitionId);
    if (existing) {
      existing.count += creature.count;
      continue;
    }
    byDefinition.set(creature.definitionId, {
      definitionId: creature.definitionId,
      keyColor: creature.keyColor,
      name: creature.name,
      ...(creature.scientificName !== undefined
        ? { scientificName: creature.scientificName }
        : {}),
      faunaClass: creature.faunaClass,
      count: creature.count,
    });
  }
  return [...byDefinition.values()].sort((a, b) => b.count - a.count);
}

// ---------------------------------------------------------------------------------------------
// Visibility & projection
// ---------------------------------------------------------------------------------------------

interface VisiblePlant {
  instance: FloraInstance;
  definition: FloraDefinition;
  growth: GrowthState;
  appearance: ManifestAppearance;
  ndcX: number;
  ndcY: number;
  distanceMeters: number;
  heightFraction: number;
}

/**
 * Project every plant, discarding those the camera cannot see: hidden, behind the camera, outside
 * the frame, or too small to matter. Describing a plant the model cannot see is worse than useless —
 * it invites the model to add one.
 */
function collectVisible(input: SceneManifestInput): VisiblePlant[] {
  const visible: VisiblePlant[] = [];

  for (const { instance, definition } of input.plants) {
    if (instance.hidden) continue;

    const growth = computeGrowthState(definition.growth, instance.maturity, {
      sizeVariation: instance.sizeVariation,
      fullness: instance.fullness,
      health: instance.health,
    });

    const base = input.project(instance.position);
    const top = input.project({
      x: instance.position.x,
      y: instance.position.y + growth.heightMeters,
      z: instance.position.z,
    });
    if (base.behindCamera || top.behindCamera) continue;

    if (Math.abs(base.x) > NDC_MARGIN || Math.abs(base.y) > NDC_MARGIN) continue;

    // NDC spans 2 units across the frame, so an NDC delta halves into a frame fraction.
    const heightFraction = Math.abs(top.y - base.y) / 2;
    if (heightFraction < MIN_HEIGHT_FRACTION) continue;

    visible.push({
      instance,
      definition,
      growth,
      appearance: describeAppearance(definition, instance, input.effectiveDayOfYear, input.resolver),
      ndcX: base.x,
      ndcY: base.y,
      distanceMeters: base.distanceMeters,
      heightFraction,
    });
  }

  return visible;
}

interface BandedPlant extends VisiblePlant {
  zone: FrameZone;
}

/**
 * Build the depth classifier for a frame.
 *
 * The depth axis uses terciles of the *visible* distance range rather than absolute meters, because
 * "foreground" means something different on a courtyard than on a five-acre site. When everything
 * sits at a similar distance the whole frame is midground, which is the honest description of a flat
 * elevation view.
 */
function depthBander(distances: readonly number[]): (distance: number) => number {
  if (distances.length === 0) return () => 0.5;

  let near = Infinity;
  let far = -Infinity;
  for (const distance of distances) {
    near = Math.min(near, distance);
    far = Math.max(far, distance);
  }
  const span = far - near;
  if (span < 5) return () => 0.5;
  return (distance) => (distance - near) / span;
}

/** Assign each plant a frame zone from the shared depth classifier. */
function applyDepthBands(
  plants: readonly VisiblePlant[],
  bandFor: (distance: number) => number,
): BandedPlant[] {
  return plants.map((plant) => ({
    ...plant,
    zone: describeZone(plant.ndcX, bandFor(plant.distanceMeters)),
  }));
}

/** Horizontal third and depth band a point falls in. Exported for tests and readouts. */
export function describeZone(ndcX: number, depthT: number): FrameZone {
  const column = ndcX < -1 / 3 ? 'left' : ndcX < 1 / 3 ? 'centre' : 'right';
  const depth = depthT < 1 / 3 ? 'foreground' : depthT < 2 / 3 ? 'midground' : 'background';
  return `${column} ${depth}` as FrameZone;
}

// ---------------------------------------------------------------------------------------------
// Drift collapsing
// ---------------------------------------------------------------------------------------------

/**
 * Collapse same-species plants sharing a zone into a single subject. Fifteen separate entries for a
 * switchgrass drift crowds out the specimen tree that actually anchors the composition; "a drift of
 * 15 Panicum virgatum across the right foreground" is both shorter and closer to how the planting
 * would be described on paper.
 *
 * The most prominent member supplies the representative dimensions and screen position.
 */
function collapseDrifts(
  plants: readonly BandedPlant[],
  speciesColors: ReadonlyMap<string, string>,
  tags: TagIndex,
): ManifestSubject[] {
  const groups = new Map<string, BandedPlant[]>();
  for (const plant of plants) {
    const key = `${plant.definition.id}::${plant.zone}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(plant);
    else groups.set(key, [plant]);
  }

  const subjects: ManifestSubject[] = [];
  for (const members of groups.values()) {
    // Largest on screen, nearest as tiebreak — the one a viewer's eye lands on.
    const lead = members.reduce((best, candidate) =>
      candidate.heightFraction > best.heightFraction ? candidate : best,
    );
    const definition = lead.definition;

    const subject: ManifestSubject = {
      definitionId: definition.id,
      keyColor: speciesColors.get(definition.id) ?? '#ffffff',
      botanicalName: definition.botanicalName,
      commonName: definition.commonNames[0] ?? definition.botanicalName,
      category: definition.category,
      geometryForm: definition.rendering.geometryForm,
      count: members.length,
      instanceIds: members.map((m) => m.instance.id),
      heightMeters: round2(lead.growth.heightMeters),
      spreadMeters: round2(lead.growth.spreadMeters),
      canopyBaseMeters: round2(lead.growth.canopyBaseMeters),
      pruningStyle: lead.instance.pruningStyle,
      appearance: lead.appearance,
      screen: {
        x: round3((lead.ndcX + 1) / 2),
        // NDC Y runs bottom-up; frame coordinates run top-down.
        y: round3(1 - (lead.ndcY + 1) / 2),
        distanceMeters: round2(lead.distanceMeters),
        heightFraction: round3(lead.heightFraction),
        zone: lead.zone,
      },
      // Manual tags on every member, not only on the lead: a drift is one line in the prompt, and a
      // tag typed onto the third grass in it would otherwise vanish when the drift collapsed.
      semantics: floraSemantics(definition, lead.instance, [
        ...members.flatMap((member) => tags.plant(member.instance.id)),
        ...tags.at(lead.instance.position.x, lead.instance.position.z),
      ]).descriptors,
    };
    if (definition.cultivar !== undefined) subject.cultivar = definition.cultivar;
    subjects.push(subject);
  }

  return subjects;
}

/**
 * Visual weight: how large it reads, boosted for nearer subjects and for drifts. Drives which
 * subjects earn an individual description when the frame is crowded.
 */
function prominence(subject: ManifestSubject): number {
  const depthBoost = 1 / (1 + subject.screen.distanceMeters / 25);
  const massBoost = 1 + Math.log10(subject.count) * 0.35;
  return subject.screen.heightFraction * depthBoost * massBoost;
}

function tally(subjects: readonly ManifestSubject[]): ManifestSpeciesTally[] {
  const byId = new Map<string, ManifestSpeciesTally>();
  for (const subject of subjects) {
    const existing = byId.get(subject.definitionId);
    if (existing) {
      existing.count += subject.count;
      continue;
    }
    byId.set(subject.definitionId, {
      definitionId: subject.definitionId,
      keyColor: subject.keyColor,
      botanicalName: subject.botanicalName,
      commonName: subject.commonName,
      count: subject.count,
    });
  }
  return [...byId.values()].sort((a, b) => b.count - a.count || a.botanicalName.localeCompare(b.botanicalName));
}

// ---------------------------------------------------------------------------------------------
// Appearance
// ---------------------------------------------------------------------------------------------

/**
 * Resolve what a plant actually looks like today. Mirrors `FloraWorld.applySeasonToView` — a plant
 * pinned by `seasonOverrideDayOfYear` ignores the global date — so the description can never disagree
 * with the pixels it accompanies.
 */
function describeAppearance(
  definition: FloraDefinition,
  instance: FloraInstance,
  globalDayOfYear: number,
  resolver?: FloraResolver,
): ManifestAppearance {
  const pinned = instance.seasonOverrideDayOfYear !== undefined;
  const day = pinned ? (instance.seasonOverrideDayOfYear as number) : globalDayOfYear;
  const offset = pinned ? 0 : instance.seasonalOffsetDays;

  // The localized season block, or the record's own when the site has not been localized. Both the
  // state and the leaf-drop figure below are read off the same block, so they cannot disagree.
  const seasons = resolver
    ? resolver.resolve(definition, instance.siteOverrides).phenology.seasons
    : definition.seasons;
  const state = resolver
    ? resolver.getPlantState(definition, globalDayOfYear, instance)
    : resolveSeasonalAppearance(definition.seasons, day, offset);
  const presence = foliagePresence(seasons, day + offset);
  const leafDropFraction = round2(1 - presence);

  const appearance: ManifestAppearance = {
    foliageColor: state.foliageColor,
    foliageColorName: describeColor(state.foliageColor),
    foliageDensity: round2(state.foliageDensity),
    branchVisibility: round2(state.branchVisibility),
    dormant: state.dormant,
    leafDropFraction,
    flowerColor: state.flowerColor,
    flowerColorName: state.flowerColor === null ? null : describeColor(state.flowerColor),
    flowerDensity: round2(state.flowerDensity),
    fruitColor: state.fruitColor,
    fruitColorName: state.fruitColor === null ? null : describeColor(state.fruitColor),
    fruitDensity: round2(state.fruitDensity),
    phrase: '',
  };
  appearance.phrase = appearancePhrase(appearance, definition.seasons.evergreen);
  return appearance;
}

function appearancePhrase(appearance: ManifestAppearance, evergreen: boolean): string {
  const parts: string[] = [];

  if (appearance.dormant) {
    parts.push('fully dormant, bare branch structure only');
  } else {
    const density =
      appearance.foliageDensity > 0.8
        ? 'dense'
        : appearance.foliageDensity > 0.5
          ? 'moderate'
          : 'sparse';
    parts.push(`${density} ${appearance.foliageColorName} foliage`);
    if (!evergreen && appearance.leafDropFraction > 0.08) {
      parts.push(`${Math.round(appearance.leafDropFraction * 100)}% leaf drop`);
    }
    if (appearance.branchVisibility > 0.35) {
      parts.push('bare branches showing through');
    }
  }

  if (appearance.flowerColor !== null && appearance.flowerDensity > 0.04) {
    // "abundant"/"scattered" rather than "heavy"/"light": a colour name can itself begin with
    // "light" or "deep", and "light light red bloom" reads as a typo, not a description.
    const weight = appearance.flowerDensity > 0.6 ? 'abundant' : 'scattered';
    parts.push(`${weight} ${appearance.flowerColorName ?? ''} bloom`.replace(/\s+/g, ' ').trim());
  }
  if (appearance.fruitColor !== null && appearance.fruitDensity > 0.04) {
    parts.push(`${appearance.fruitColorName ?? ''} fruit set`.replace(/\s+/g, ' ').trim());
  }

  return parts.join(', ');
}

// ---------------------------------------------------------------------------------------------
// Environment & camera prose
// ---------------------------------------------------------------------------------------------

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const COMPASS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];

/** Nearest 8-point compass name for a bearing in degrees clockwise from north. */
export function compassName(degrees: number): string {
  const normalised = ((degrees % 360) + 360) % 360;
  const index = Math.round(normalised / 45) % 8;
  return COMPASS[index] ?? 'north';
}

function describeEnvironment(state: EnvironmentState, effectiveDay: number): ManifestEnvironment {
  const sun = computeSunPosition(state.time, state.latitudeDegrees, state.date.dayOfYear);
  const { month, day } = dateFromDayOfYear(effectiveDay);
  const blend = seasonBlend(effectiveDay);

  // Within a bracket, the first quarter still reads as "late <from>" and the last as "early <to>".
  const seasonLabel =
    blend.t < 0.25
      ? `early ${blend.from}`
      : blend.t < 0.6
        ? `mid ${blend.from}`
        : blend.t < 0.85
          ? `late ${blend.from}`
          : `early ${blend.to}`;

  const hour12 = state.time.hour % 12 === 0 ? 12 : state.time.hour % 12;
  const meridiem = state.time.hour < 12 ? 'am' : 'pm';
  const timeLabel = `${hour12}:${String(state.time.minute).padStart(2, '0')} ${meridiem}`;

  return {
    timeLabel,
    hour: state.time.hour,
    minute: state.time.minute,
    dayOfYear: effectiveDay,
    dateLabel: `${MONTH_NAMES[month - 1] ?? 'January'} ${day}`,
    seasonLabel,
    weather: state.weather,
    // Gated on the same window the renderer gates on, so the prompt can never describe snow the
    // viewport is not showing.
    groundCover: isFreezingSeason(effectiveDay) ? describeGroundCover(state.groundCover) : null,
    latitudeDegrees: state.latitudeDegrees,
    sunAzimuthDegrees: round1(sun.azimuthDegrees),
    sunElevationDegrees: round1(sun.elevationDegrees),
    lightPhrase: describeLight(sun.azimuthDegrees, sun.elevationDegrees, state.weather),
  };
}

function describeLight(azimuth: number, elevation: number, weather: string): string {
  if (elevation <= -6) return 'night, no direct sun — cool ambient sky light only';
  if (elevation <= 0) return `twilight, sun just below the ${compassName(azimuth)} horizon`;

  const from = compassName(azimuth);
  const quality =
    elevation < 10
      ? 'low golden light with long raking shadows'
      : elevation < 25
        ? 'warm low-angle light with long shadows'
        : elevation < 50
          ? 'mid-height sun with defined shadows'
          : 'high overhead sun with short shadows';

  const diffused =
    weather === 'overcast'
      ? ', heavily diffused by overcast cloud'
      : weather === 'after-rain'
        ? ', breaking through after rain'
        : '';

  return `${quality} from the ${from}, ${Math.round(elevation)}° above the horizon${diffused}`;
}

function describeCamera(camera: Omit<ManifestCamera, 'phrase'>): string {
  const eye =
    camera.heightMeters < 2.2
      ? 'eye-level'
      : camera.heightMeters < 8
        ? 'slightly elevated'
        : camera.heightMeters < 25
          ? 'elevated'
          : 'high aerial';
  const tilt =
    camera.pitchDegrees < -45
      ? 'looking steeply down'
      : camera.pitchDegrees < -12
        ? 'looking down'
        : camera.pitchDegrees > 8
          ? 'looking slightly up'
          : 'looking level toward the horizon';
  return `${eye} view from about ${Math.round(camera.distanceMeters)} m, ${tilt}, ${Math.round(camera.fieldOfViewDegrees)}° field of view`;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}
